import { Cart, CartItem, Pricing } from "@dining/api-client";
import { Body1Strong, Button, Divider } from "@fluentui/react-components";
import React, { useEffect, useState, useRef } from "react";
import { CartEntry } from "./CartEntry";
import { useStyles } from "./useStyles";
import { HelperUtils } from "../../../common";

type CartEntriesProps = {
  cart?: Cart;
  onAddItem: () => void;
  onRemoveItem: (
    cafeId: string,
    itemId: string,
    buttonRef?: React.RefObject<HTMLButtonElement>,
  ) => void;
  onEditItem?: (cafeID: string, itemId: string, id: string) => void;
};

export const CartEntries = ({ cart, onAddItem, onRemoveItem, onEditItem }: CartEntriesProps) => {
  const styles = useStyles();
  const [isCartEmpty, setIsCartEmpty] = useState(true);
  useEffect(() => {
    if (!cart || cart.items.length === 0) {
      setIsCartEmpty(true);
    } else {
      setIsCartEmpty(false);
    }
  }, [cart]);
  const helperUtils = HelperUtils.getInstance();
  const getSelectedOptions = (cartItem: CartItem) => {
    const selectedOptions = cartItem.options?.reduce((acc, option) => {
      const selectedOption = option?.options?.find((option) => option.isSelected);
      if (selectedOption) {
        acc.push(selectedOption.name);
      }
      return acc;
    }, [] as string[]);
    return selectedOptions?.join(", ");
  };
  const removeButtonRefs = useRef<{ [key: string]: React.RefObject<HTMLButtonElement> }>({});
  // Helper to get or create a ref for a given item id
  const getRemoveButtonRef = (id: string) => {
    if (!removeButtonRefs.current[id]) {
      removeButtonRefs.current[id] = React.createRef<HTMLButtonElement>();
    }
    return removeButtonRefs.current[id];
  };
  return (
    <div className={styles.container} data-testid="CartEntries">
      <div className={styles.header}>
        <Body1Strong className={styles.sectionTitle}>Review your order</Body1Strong>
        <Button
          appearance="subtle"
          aria-label="Add Item"
          className={styles.addIcon}
          data-testid="AddItemButton"
          onClick={onAddItem}
          tabIndex={6}
        >
          Add
        </Button>
      </div>
      <div className={styles.list}>
        {isCartEmpty && <CartEntry label={"Your cart is empty"} />}

        {!isCartEmpty &&
          cart?.items &&
          cart.items.length > 0 &&
          cart.items.map((item, index) => {
            const dietary = helperUtils.getDietaryLabelStr(item);
            const dietaryAccessible = helperUtils.getDietaryLabelAccessibleStr(item);
            return (
              <CartEntry
                cafeId={cart.cafeId}
                key={item.id}
                label={`${item.name}${dietary ? " " + dietary : ""}`}
                accessibleLabel={`${item.name}${dietaryAccessible ? " " + dietaryAccessible : ""}`}
                customInstructions={item.customInstruction}
                itemId={item.itemId}
                id={item.id}
                subTitle={getSelectedOptions(item)}
                amount={helperUtils.getPricingStr(
                  Pricing.fromJS({
                    amount: item.amountWithOptions,
                    currencyUnit: item.pricing?.currencyUnit,
                  }),
                )}
                divStatus={index <= cart.items.length - 2}
                removeButtonRef={item.id ? getRemoveButtonRef(item.id) : undefined}
                onRemove={(cafeId, itemId) =>
                  onRemoveItem(cafeId, itemId, removeButtonRefs.current[item.id])
                }
                onEdit={onEditItem}
                amountClassName={styles.itemAmount}
              />
            );
          })}

        {!isCartEmpty && <Divider />}
        {!isCartEmpty && cart?.taxAmount && (
          <CartEntry
            key={"tax"}
            label={"Tax"}
            amount={helperUtils.getPricingStr(cart.taxAmount)}
            amountClassName={styles.taxAmount}
          />
        )}
        {!isCartEmpty && <Divider appearance="strong" />}
        {!isCartEmpty && cart?.totalAmount && (
          <CartEntry
            key={"total"}
            label={"Total"}
            amount={helperUtils.getPricingStr(cart.totalAmount)}
            amountClassName={styles.totalAmount}
          />
        )}
      </div>
    </div>
  );
};
